/*
    Copyright 2022-2025, Techwise Integrated Security, LLC
*/

import React, { useState } from "react";

import Typography from "@mui/material/Typography";

import { Box, Fade, Modal, Table, TableBody, TableCell, TableFooter, TableHead, TablePagination, TableRow } from "@mui/material";
import Backdrop from "@mui/material/Backdrop";
import { ModalBase } from "../../utils/Interfaces";

export interface TableModalProps<T> extends ModalBase {
    title: string;
    columns: TableColumn<T>[];
    data: T[];
    rowsPerPage: number;
    footer?: JSX.Element | string;
}

export interface TableColumn<E> {
    header: string;
    width: string | number;
    field: keyof E;
}

const TableModal = <V,>(props: TableModalProps<V>) => {
    const { data, title, columns, rowsPerPage, open, handleClose, footer = "" } = props;

    const [page, setPage] = useState(0);

    return (
        <Modal
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
            open={open}
            onClose={handleClose}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout: 500,
            }}
        >
            <Fade in={open}>
                <Box
                    sx={{
                        backgroundColor: (theme) => theme.palette.background.paper,
                        border: "1px solid #000",
                        boxShadow: (theme) => theme.shadows[5],
                        padding: (theme) => theme.spacing(2, 4, 3),
                        zIndex: 1000,
                        minWidth: "60vw",
                    }}
                >
                    <Typography variant="h5">{title}</Typography>

                    <Box sx={{ marginBottom: "20px" }} />

                    <Table size="small" aria-label="a dense table">
                        <TableHead>
                            <TableRow>
                                {columns.map((column: TableColumn<V>, i: number) => {
                                    return (
                                        <TableCell key={i} sx={{ width: column.width }}>
                                            <Typography>{column.header}</Typography>
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row: V, index: number) => (
                                <TableRow
                                    key={index}
                                    sx={{
                                        "&:last-child td, &:last-child th": {
                                            border: 0,
                                        },
                                    }}
                                >
                                    {columns.map((column: TableColumn<V>, j: number) => (
                                        <TableCell key={j}>
                                            <Typography>{(row[column.field] as object).toString()}</Typography>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                        <TableFooter>
                            <TableRow>
                                <TablePagination
                                    colSpan={3}
                                    count={data.length}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    labelRowsPerPage=""
                                    onPageChange={(event, newPage) => setPage(newPage)}
                                    showFirstButton={false}
                                    showLastButton={false}
                                    SelectProps={{
                                        sx: { display: "none" },
                                    }}
                                />
                            </TableRow>
                        </TableFooter>
                    </Table>

                    {footer !== "" && (
                        <Box sx={{ mt: 2 }}>
                            <Typography component="span">{footer}</Typography>
                        </Box>
                    )}
                </Box>
            </Fade>
        </Modal>
    );
};

export default TableModal;
